import {CATEGORY_COLORS} from "@/theme/engine.mjs";
import WriteButton from "@/components/WriteButton";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tags } from "lucide-react";

const COLORS = CATEGORY_COLORS;

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: COLORS[0], icon: "Package" });

  const load = async () => {
    try { setCats((await api.get("/categories")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", color: COLORS[0], icon: "Package" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, color: c.color, icon: c.icon }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama kategori wajib diisi");
    try {
      if (editing) await api.put(`/categories/${editing.id}`, form);
      else await api.post("/categories", form);
      toast.success("Kategori disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"? Produk terkait akan tanpa kategori.`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Kategori dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <PageHeader title="Kategori" subtitle={`${cats.length} kategori`}>
        <WriteButton data-testid="add-category-btn" onClick={openNew} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
          <Plus className="h-4 w-4" /> Tambah Kategori
        </WriteButton>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="category-management">
          {cats.map((c) => (
            <div key={c.id} data-testid={`category-card-${c.id}`} className="rounded-xl border border-ui-line bg-ui-soft p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: c.color + "22", color: c.color }}>
                  <Tags className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-ui-muted">{c.product_count} produk</div>
                </div>
              </div>
              <div className="flex gap-1">
                <WriteButton plain data-testid={`edit-category-${c.id}`} onClick={() => openEdit(c)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-ui-muted-surface flex items-center justify-center"><Pencil className="h-4 w-4 text-ui-muted" /></WriteButton>
                <WriteButton plain data-testid={`delete-category-${c.id}`} onClick={() => remove(c)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center"><Trash2 className="h-4 w-4 text-danger-readable" /></WriteButton>
              </div>
            </div>
          ))}
          {cats.length === 0 && <p className="text-ui-muted col-span-full text-center py-10">Belum ada kategori</p>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-form-dialog" className="bg-ui-card border-ui-line text-ui-text max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Kategori" : "Tambah Kategori"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Nama</Label>
              <Input data-testid="category-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ui-card border-ui-line" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Warna</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((col) => (
                  <button key={col} onClick={() => setForm({ ...form, color: col })}
                    className={`h-9 w-9 rounded-lg border-2 ${form.color === col ? "border-ui-line" : "border-transparent"}`}
                    style={{ backgroundColor: col }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-ui-line bg-transparent hover:bg-ui-card">Batal</Button>
            <WriteButton data-testid="save-category-btn" onClick={save} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">Simpan</WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
